// PaymentMethodButton. Color-coded button for selecting a payment method
// in the sale panel. See DESIGN.md §9.8 (panel layout) and §9.13
// (color mapping). Replaces the inline paymentMethodButtonStyle helper.

import type { CSSProperties, ReactNode } from "react";

export type PaymentMethodColor = "cash" | "transfer" | "card" | "charge" | "magenta";

type Props = {
  name: string;
  color: PaymentMethodColor;
  icon?: ReactNode;
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  onClick: () => void;
  style?: CSSProperties;
};

const colorMap: Record<
  PaymentMethodColor,
  { bg: string; bgHover: string }
> = {
  cash: { bg: "var(--color-cash)", bgHover: "var(--color-cash-hover)" },
  transfer: {
    bg: "var(--color-transfer)",
    bgHover: "var(--color-transfer-hover)",
  },
  card: { bg: "var(--color-card)", bgHover: "var(--color-card-hover)" },
  charge: {
    bg: "var(--color-charge)",
    bgHover: "var(--color-charge-hover)",
  },
  magenta: {
    bg: "var(--color-magenta)",
    bgHover: "var(--color-magenta-hover)",
  },
};

const baseStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "var(--space-2)",
  border: "none",
  borderRadius: "var(--radius-md)",
  padding: "var(--space-3) var(--space-4)",
  minHeight: 50,
  color: "var(--text-inverse)",
  fontSize: "var(--font-size-15)",
  fontWeight: 600,
  cursor: "pointer",
  transition:
    "background 120ms, color 120ms, transform 80ms, opacity 120ms",
};

function Spinner() {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: 16,
        height: 16,
        border: "2px solid currentColor",
        borderRightColor: "transparent",
        borderRadius: "var(--radius-pill)",
        animation: "vl-spin 0.8s linear infinite",
      }}
    />
  );
}

export function PaymentMethodButton({
  name,
  color,
  icon,
  loading = false,
  disabled = false,
  fullWidth,
  onClick,
  style,
}: Props) {
  const palette = colorMap[color];

  const computed: CSSProperties = {
    ...baseStyle,
    background: palette.bg,
    width: fullWidth ? "100%" : undefined,
    opacity: disabled || loading ? (loading ? 1 : 0.4) : 1,
    cursor: disabled ? "not-allowed" : "pointer",
  };

  const onMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || loading) return;
    e.currentTarget.style.background = palette.bgHover;
  };
  const onMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || loading) return;
    e.currentTarget.style.background = palette.bg;
  };
  const onMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || loading) return;
    e.currentTarget.style.transform = "scale(0.98)";
  };
  const onMouseUp = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.transform = "scale(1)";
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      style={{ ...computed, ...style }}
    >
      {loading ? (
        <>
          <Spinner />
          <span>Procesando...</span>
        </>
      ) : (
        <>
          {icon ? (
            <span
              aria-hidden="true"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 20,
                height: 20,
              }}
            >
              {icon}
            </span>
          ) : null}
          <span>{name}</span>
        </>
      )}
    </button>
  );
}
